import {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  BaseQueryFn,
} from '@reduxjs/toolkit/dist/query';
import { api } from '../../app/services/api';
import defaultCellOrder from '../../utils/sortCellTypes';

export enum HardCodedCellTypes {
  unknown = 'Unknown Application Group',
}

export interface IGetControllerDataRequest {
  store_num: number;
  controller_name: string;
}

export interface IApplicationData {
  application: string;
  cell_type: string | null;
  controller_name: string;
  controller_type: string;
  data_log_date: string;
  data_value: string | number | null;
  eng_unit: string | null;
  last_updated: string;
  property: string;
  store_num: number;
  offline: number;
  in_alarm: number;
}

export type GetControllerDataResponseType = IApplicationData[];

export type UpdateAppCellTypeRequest = {
  store_num: number;
  controller_name: string;
  application: string;
  cell_type: string;
};

export type SetApplicationCellTypeToUnknownRequestType = {
  store_num: number;
  controller_name: string;
  application: string;
  cell_type: string; // the current cell_type assigned to the application
};

export type MergeApplicationRequestType = {
  store_num: number;
  controller_name: string;
  old_application: string;
  new_application: string;
  cell_type: string;
};

export type DeleteControllerRequestType = {
  store_num: number;
  controller_name: string;
};

export type DeleteApplicationRequestType = {
  store_num: number;
  controller_name: string;
  application: string;
};

export type SetControllerTypeRequestType = {
  store_num: number;
  controller_name: string;
  controller_type: string;
};

export type GetControllerTypesRequestType = {
  store_list: number[];
};

export type GetControllerCellTypesResponseType = { cell_type: string | null }[];

export const controllerApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getLatestControllerData: builder.query<
      GetControllerDataResponseType,
      IGetControllerDataRequest
    >({
      query: ({ store_num, controller_name }) => ({
        url: 'controllers/data-latest',
        method: 'POST',
        body: {
          store_num,
          controller_name,
        },
      }),
      providesTags: ['Applications'],
    }),
    getLatestControllerDataArr: builder.query<
      GetControllerDataResponseType,
      IGetControllerDataRequest[]
    >({
      queryFn: async (args, _api, _extraOptions, baseQuery) => {
        let results: GetControllerDataResponseType = [];
        try {
          for (const arg of args) {
            const res = (await baseQuery({
              url: 'controllers/data-latest',
              method: 'POST',
              body: arg,
            })) as Awaited<
              ReturnType<
                BaseQueryFn<
                  IGetControllerDataRequest,
                  GetControllerDataResponseType,
                  FetchBaseQueryError,
                  void,
                  FetchBaseQueryMeta
                >
              >
            >;
            if (res.error) {
              return { error: res.error };
            }
            if (res.data) {
              results = [...results, ...res.data];
            }
          }
          return { data: results };
        } catch (err: unknown) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return { error: err as any };
        }
      },
    }),
    updateApplicationCellType: builder.mutation<
      unknown,
      UpdateAppCellTypeRequest
    >({
      query: (args) => ({
        url: 'controllers/cell-type',
        method: 'POST',
        body: args,
      }),
      invalidatesTags: ['Applications'],
    }),
    setApplicationCellTypeToUnknown: builder.mutation<
      // ASK JITHIN HOW THIS IS DIFF
      unknown,
      SetApplicationCellTypeToUnknownRequestType
    >({
      query: (args) => ({
        url: 'controllers/cell-type',
        method: 'POST',
        body: args,
      }),
      invalidatesTags: ['Applications'],
    }),
    mergeApplication: builder.mutation<unknown, MergeApplicationRequestType>({
      query: (args) => ({
        url: 'controllers/application-merge',
        method: 'POST',
        body: args,
      }),
      invalidatesTags: ['Applications'],
    }),
    deleteController: builder.mutation<unknown, DeleteControllerRequestType>({
      query: (args) => ({
        url: 'controllers/controller',
        method: 'DELETE',
        body: args,
      }),
      invalidatesTags: ['Applications', 'Sites'], // not sure this is the correct tag. may need a new one
    }),
    deleteApplication: builder.mutation<unknown, DeleteApplicationRequestType>({
      query: (args) => ({
        url: 'controllers/applications',
        method: 'DELETE',
        body: args,
      }),
      invalidatesTags: ['Applications'],
    }),
    setControllerType: builder.mutation<unknown, SetControllerTypeRequestType>({
      query: (args) => ({
        url: 'controllers/type',
        method: 'PATCH',
        body: args,
      }),
      invalidatesTags: ['Applications', 'Sites'],
    }),
    getControllerTypes: builder.query<string[], GetControllerTypesRequestType>({
      query: (args) => ({
        url: 'controllers/type',
        method: 'POST',
        body: args,
      }),
    }),
    getCellTypes: builder.query<Array<string>, GetControllerTypesRequestType>({
      query: (body) => ({
        url: 'controllers/cell-type',
        method: 'POST',
        body,
      }),
      transformResponse: (response: GetControllerCellTypesResponseType) => {
        const cellTypes = response.map((object) => object.cell_type);
        const cleanedCellTypes = cellTypes
          .filter((cellType) => cellType !== null)
          .map((cellType) => cellType.toLowerCase());
        const uniqueCellTypes = Array.from(new Set(cleanedCellTypes));
        uniqueCellTypes.push(
          ...[HardCodedCellTypes.unknown.toLowerCase(), 'inactive'],
        ); // need to make sure strings match exactly
        return uniqueCellTypes.sort(defaultCellOrder);
      },
    }),
    getCellTypesWithoutTransform: builder.query<
      Array<string>,
      GetControllerTypesRequestType
    >({
      query: (body) => ({
        url: 'controllers/cell-type',
        method: 'POST',
        body,
      }),
      transformResponse: (response: GetControllerCellTypesResponseType) => {
        const cellTypes = response.map((object) => object.cell_type);
        const cleanedCellTypes = cellTypes.filter(
          (cellType) => cellType !== null,
        );
        const uniqueCellTypes = Array.from(new Set(cleanedCellTypes));

        return uniqueCellTypes;
      },
    }),
  }),
});

export const {
  useGetLatestControllerDataQuery,
  useGetLatestControllerDataArrQuery,
  useUpdateApplicationCellTypeMutation,
  useSetApplicationCellTypeToUnknownMutation,
  useMergeApplicationMutation,
  useDeleteControllerMutation,
  useDeleteApplicationMutation,
  useSetControllerTypeMutation,
  useGetControllerTypesQuery,
  useGetCellTypesQuery,
  useGetCellTypesWithoutTransformQuery,
} = controllerApi;
